import {intlShape, utils} from '@wallaby-ui/core';
import {findIndex} from 'lodash';
import PropTypes from 'prop-types';
import * as React from 'react';
import {defineMessages, FormattedMessage, injectIntl} from 'react-intl';

import styles from './PdfViewerToolbar.less';
import PdfViewerToolbarButton from './PdfViewerToolbarButton';
import zoomOptions from 'src/js/lib/pdf_viewer/zoom_options.json';

export const I18N = defineMessages({
  ROTATE_LEFT: {
    id: 'document.pdf.rotateLeft',
    defaultMessage: 'Rotate left',
    description: 'text for pdf toolbar rotate left  button',
  },
  ROTATE_RIGHT: {
    id: 'document.pdf.rotate',
    defaultMessage: 'Rotate right',
    description: 'text for pdf toolbar rotate right button',
  },
  ZOOM: {
    id: 'document.pdf.zoom',
    defaultMessage: 'Zoom',
    description: 'text for pdf toolbar zoom block',
  },
  ZOOM_IN: {
    id: 'document.pdf.zoomIn',
    defaultMessage: 'Zoom In',
    description: 'text for pdf toolbar zoom in button',
  },
  ZOOM_OUT: {
    id: 'document.pdf.zoomOut',
    defaultMessage: 'Zoom Out',
    description: 'text for pdf toolbar zoom out button',
  },
  PAGE: {
    id: 'document.pdf.page',
    defaultMessage: 'Page',
    description: 'text for pdf toolbar page block',
  },
  FIRST_PAGE: {
    id: 'document.pdf.firstPage',
    defaultMessage: 'First page',
    description: 'text for pdf toolbar first page button',
  },
  PREVIOUS_PAGE: {
    id: 'document.pdf.previousPage',
    defaultMessage: 'Previous page',
    description: 'text for pdf toolbar previous page button',
  },
  NEXT_PAGE: {
    id: 'document.pdf.nextPage',
    defaultMessage: 'Next page',
    description: 'text for pdf toolbar next page button',
  },
  LAST_PAGE: {
    id: 'document.pdf.lastPage',
    defaultMessage: 'Last page',
    description: 'text for pdf toolbar last page button',
  },
});

class PdfViewerToolbar extends React.PureComponent {
  constructor() {
    super();

    this.state = {
      pageNumber: 1,
      zoom: '',
      pagingEnabled: true,
      pagesCount: 1,
      nextPageEnabled: false,
      previousPageEnabled: false,
      rotation: 0,
      zoomInDisabled: false,
      zoomOutDisabled: false,
    };

    this.onPagesInit = this.onPagesInit.bind(this);
    this.onPageChanged = this.onPageChanged.bind(this);
    this.previousPage = this.previousPage.bind(this);
    this.firstPage = this.firstPage.bind(this);
    this.lastPage = this.lastPage.bind(this);
    this.manualPage = this.manualPage.bind(this);
    this.nextPage = this.nextPage.bind(this);
    this.rotate = this.rotate.bind(this);
    this.rotateClockwise = this.rotateClockwise.bind(this);
    this.zoom = this.zoom.bind(this);
    this.zoomIn = this.zoomIn.bind(this);
    this.zoomOut = this.zoomOut.bind(this);
    this.handlePageNumberChange = this.handlePageNumberChange.bind(this);
    this.handleZoomChange = this.handleZoomChange.bind(this);

    this.pageInputWidth = {width: '36px'};
  }

  componentDidMount() {
    const {initEvent} = this.props;
    initEvent('pagesinit', this.onPagesInit);
    initEvent('pagechange', this.onPageChanged);
  }

  componentWillUnmount() {
    const {removeEvent} = this.props;
    removeEvent('pagesinit', this.onPagesInit);
    removeEvent('pagechange', this.onPageChanged);
  }

  handleZoomChange(event) {
    this.setState({zoom: event.target.value});
    if (this.zoom) {
      const idx = this.findZoomIndex(event.target.value);
      this.updateZoomButtons(idx);
      this.zoom(event.target.value);
    }
  }

  handlePageNumberChange(event) {
    const pageNumber = Number(event.target.value);
    this.setState({pageNumber});
    if (this.manualPage) {
      this.manualPage(pageNumber);
    }
  }

  onPagesInit(payload) {
    const pagesCount = payload.source.getPagesCount();
    this.setState({
      pagesCount,
      nextPageEnabled: pagesCount > 1,
      pagingEnabled: pagesCount > 1,
      rotation: payload.source.getRotation(),
      zoom: payload.source.currentScaleValue || payload.source.currentScale,
    });
  }

  onPageChanged(payload) {
    const {pagesCount} = this.state;
    const pageNumber = Number(payload.pageNumber);
    this.setState({
      pageNumber,
      nextPageEnabled: pageNumber < pagesCount,
      previousPageEnabled: pageNumber > 1,
    });
  }

  previousPage() {
    const {onSetPage} = this.props;
    const {pageNumber, previousPageEnabled} = this.state;
    if (previousPageEnabled) {
      const newPageNumber = Number(pageNumber) - 1;
      onSetPage(newPageNumber);
      this.setState({pageNumber: newPageNumber});
    }
  }

  nextPage() {
    const {onSetPage} = this.props;
    const {nextPageEnabled, pageNumber} = this.state;
    if (nextPageEnabled) {
      const newPageNumber = Number(pageNumber) + 1;
      onSetPage(newPageNumber);
      this.setState({pageNumber: newPageNumber});
    }
  }

  manualPage(newPageNumber) {
    const {onSetPage} = this.props;
    const {pagesCount} = this.state;
    if (
      typeof newPageNumber === 'number' &&
      !!newPageNumber &&
      newPageNumber <= pagesCount
    ) {
      onSetPage(newPageNumber);
    }
  }

  rotate(inverse) {
    const {onRotate} = this.props;
    const {rotation} = this.state;
    const newRotation = (rotation + (!inverse || -1) * 450) % 360;
    this.setState({rotation: newRotation});
    onRotate(newRotation);
  }

  rotateClockwise() {
    this.rotate(true);
  }

  zoom(newZoom) {
    const {onZoom} = this.props;
    onZoom(newZoom);
  }

  firstPage() {
    const {onSetPage} = this.props;
    const newPageNumber = 1;
    onSetPage(newPageNumber);
    this.setState({pageNumber: newPageNumber});
  }

  lastPage() {
    const {onSetPage} = this.props;
    const {pagesCount} = this.state;
    const newPageNumber = pagesCount;
    onSetPage(newPageNumber);
    this.setState({pageNumber: newPageNumber});
  }

  zoomIn() {
    let idx = this.findZoomIndex();
    let newZoom;
    if (idx < 3) {
      // reset to 100%
      idx = 5;
      newZoom = 1;
    } else if (idx + 1 < zoomOptions.length) {
      idx += 1;
      newZoom = zoomOptions[idx].value;
    }
    this.setState({zoom: newZoom});
    this.updateZoomButtons(idx);
    this.zoom(newZoom);
  }

  zoomOut() {
    const {zoom} = this.state;
    let idx = this.findZoomIndex();
    let newZoom = zoom;
    if (idx > 3) {
      idx -= 1;
      newZoom = zoomOptions[idx].value;
    }
    this.setState({zoom: newZoom});
    this.updateZoomButtons(idx);
    this.zoom(newZoom);
  }

  findZoomIndex(value) {
    const {zoom} = this.state;
    return findIndex(
      zoomOptions,
      (item) => item.value.toString() === (value || zoom.toString())
    );
  }

  updateZoomButtons(zoomIdx) {
    const idx = zoomIdx || this.findZoomIndex();
    if (idx + 1 === zoomOptions.length) {
      this.setState({
        zoomInDisabled: true,
        zoomOutDisabled: false,
      });
    } else if (idx < 4) {
      this.setState({
        zoomInDisabled: false,
        zoomOutDisabled: true,
      });
    } else {
      this.setState({
        zoomInDisabled: false,
        zoomOutDisabled: false,
      });
    }
  }

  render() {
    const {intl} = this.props;
    const {
      nextPageEnabled,
      pagingEnabled,
      pagesCount,
      pageNumber,
      previousPageEnabled,
      zoom,
      zoomInDisabled,
      zoomOutDisabled,
    } = this.state;
    return (
      <div className={styles.dlPdfViewerToolbar}>
        <div className={'pdf-viewer-toolbar__wrapper'}>
          <ul className={styles.dlPdfViewerToolbarList}>
            <li>
              <PdfViewerToolbarButton
                clickPressCallback={this.rotate}
                titleText={utils.TranslateUtils.translate(
                  I18N.ROTATE_LEFT,
                  intl
                )}
                iconClass={'fas fa-undo'}
              />
            </li>
            <li>
              <PdfViewerToolbarButton
                clickPressCallback={this.rotateClockwise}
                titleText={utils.TranslateUtils.translate(
                  I18N.ROTATE_RIGHT,
                  intl
                )}
                iconClass={'fas fa-redo'}
              />
            </li>
            <li>
              <span className={'pdf-viewer-toolbar__zoom-input'}>
                <label htmlFor={'zoomInput'}>
                  <span>
                    <FormattedMessage {...I18N.ZOOM} />
                    {':'}
                  </span>
                </label>
                <select
                  id={'zoomInput'}
                  className={'pt__timestamp-selectbox'}
                  value={zoom}
                  onChange={this.handleZoomChange}
                >
                  {zoomOptions.map((item, idx) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <option value={item.value} key={idx}>
                      {item.label}
                    </option>
                  ))}
                </select>
                <PdfViewerToolbarButton
                  clickPressCallback={this.zoomOut}
                  titleText={utils.TranslateUtils.translate(
                    I18N.ZOOM_OUT,
                    intl
                  )}
                  iconClass={'fas fa-search-minus'}
                  disabled={zoomOutDisabled}
                />
                <PdfViewerToolbarButton
                  clickPressCallback={this.zoomIn}
                  titleText={utils.TranslateUtils.translate(I18N.ZOOM_IN, intl)}
                  disabled={zoomInDisabled}
                  iconClass={'fas fa-search-plus'}
                />
              </span>
            </li>
            {pagingEnabled ? (
              <li>
                <span className={'pdf-viewer-toolbar__page-input'}>
                  <label htmlFor={'pageNumberInput'}>
                    <span>
                      <FormattedMessage {...I18N.PAGE} />
                      {':'}
                    </span>
                  </label>
                  <input
                    id={'pageNumberInput'}
                    type={'number'}
                    className={'selected-page'}
                    min={'1'}
                    max={pagesCount}
                    value={pageNumber}
                    onChange={this.handlePageNumberChange}
                    style={this.pageInputWidth}
                  />
                  {' / '}
                  {pagesCount}
                </span>
              </li>
            ) : null}
            {pagingEnabled ? (
              <li>
                <PdfViewerToolbarButton
                  clickPressCallback={this.firstPage}
                  titleText={utils.TranslateUtils.translate(
                    I18N.FIRST_PAGE,
                    intl
                  )}
                  iconClass={'fas fa-fast-backward'}
                  disabled={!previousPageEnabled}
                />
              </li>
            ) : null}
            {pagingEnabled ? (
              <li>
                <PdfViewerToolbarButton
                  clickPressCallback={this.previousPage}
                  titleText={utils.TranslateUtils.translate(
                    I18N.PREVIOUS_PAGE,
                    intl
                  )}
                  iconClass={'fas fa-step-backward'}
                  disabled={!previousPageEnabled}
                />
              </li>
            ) : null}
            {pagingEnabled ? (
              <li>
                <PdfViewerToolbarButton
                  clickPressCallback={this.nextPage}
                  titleText={utils.TranslateUtils.translate(
                    I18N.NEXT_PAGE,
                    intl
                  )}
                  iconClass={'fas fa-step-forward'}
                  disabled={!nextPageEnabled}
                />
              </li>
            ) : null}
            {pagingEnabled ? (
              <li>
                <PdfViewerToolbarButton
                  clickPressCallback={this.lastPage}
                  titleText={utils.TranslateUtils.translate(
                    I18N.LAST_PAGE,
                    intl
                  )}
                  iconClass={'fas fa-fast-forward'}
                  disabled={!nextPageEnabled}
                />
              </li>
            ) : null}
          </ul>
        </div>
      </div>
    );
  }
}

PdfViewerToolbar.displayName = 'PdfViewerToolbar';

PdfViewerToolbar.propTypes = {
  intl: intlShape.isRequired,
  onSetPage: PropTypes.func.isRequired,
  onRotate: PropTypes.func.isRequired,
  onZoom: PropTypes.func.isRequired,
  initEvent: PropTypes.func.isRequired,
};

export default injectIntl(PdfViewerToolbar);
